function getApiBaseUrl() {
  return window.__DIGITAL_EMPLOYEE_API_URL__ || "";
}

function getSalesBaseUrl() {
  return window.location.origin || "";
}

/** Map IANA timezone to ISO country code for popular countries. */
const TZ_TO_COUNTRY = {
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Los_Angeles": "US", "America/Phoenix": "US", "America/Anchorage": "US",
  "Pacific/Honolulu": "US", "America/Detroit": "US", "America/Indiana/Indianapolis": "US",
  "America/Boise": "US",
  "Europe/London": "GB", "Europe/Belfast": "GB",
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA",
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Brisbane": "AU",
  "Australia/Perth": "AU", "Australia/Adelaide": "AU",
  "Europe/Berlin": "DE", "Europe/Munich": "DE",
  "Europe/Paris": "FR",
  "Asia/Singapore": "SG",
  "Asia/Dubai": "AE",
  "America/Sao_Paulo": "BR", "America/Fortaleza": "BR",
  "Asia/Tokyo": "JP",
  "Europe/Amsterdam": "NL",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "America/Mexico_City": "MX", "America/Cancun": "MX", "America/Monterrey": "MX",
  "Asia/Seoul": "KR",
  "Asia/Riyadh": "SA",
  "Europe/Zurich": "CH",
  "Europe/Stockholm": "SE",
  "Europe/Dublin": "IE",
};

function detectCountryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_COUNTRY[tz] || null;
  } catch { return null; }
}

export async function detectCountry() {
  const origin = getSalesBaseUrl();
  if (origin) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`${origin}/api/v1/public/geo/detect`, {
        cache: "no-cache",
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.country_code) return data.country_code;
      }
    } catch {}
  }
  return detectCountryFromTimezone();
}

async function publicRequest(path, options = {}, base_url = null) {
  const base = base_url || getApiBaseUrl();
  const url = base ? `${base.replace(/\/$/, "")}${path}` : path;
  const { headers: extraHeaders, ...rest } = options;
  const res = await fetch(url, {
    cache: "no-cache",
    ...rest,
    headers: { "Content-Type": "application/json", ...extraHeaders },
  });
  if (!res.ok) {
    const text = await res.text();
    let errBody;
    try {
      errBody = JSON.parse(text);
    } catch {
      errBody = { message: text };
    }
    throw new Error(
      typeof errBody.message === "string"
        ? errBody.message
        : `Request failed: ${res.status} ${res.statusText}`,
    );
  }
  return res.json();
}

export async function getEmployeeBySlug(slug) {
  const res = await publicRequest(`/v1/public/employees/${slug}`);
  return res.employee;
}

function getMetaCookie(name) {
  if (typeof document === "undefined" || !document.cookie) return null;
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export async function createSession(slug, visitor) {
  const raw = await publicRequest("/v1/public/meetings/start", {
    method: "POST",
    body: JSON.stringify({ share_slug: slug }),
  });
  const startRes = raw.meeting;

  await publicRequest(`/v1/public/meetings/${startRes.meeting_id}/attendee`, {
    method: "POST",
    headers: { "X-Session-ID": startRes.session_id },
    body: JSON.stringify({
      room_name: startRes.room_name,
      name: visitor.name,
      email: visitor.email,
      phone: visitor.phone,
    }),
  });

  const nameParts =
    typeof visitor.name === "string" ? visitor.name.trim().split(/\s+/) : [];
  const firstName = nameParts[0] ?? null;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : null;
  sendMetaConversionEvent("AI Demo Lead", {
    fbp:
      typeof window !== "undefined"
        ? (window.__META_FBP__ ?? getMetaCookie("_fbp"))
        : null,
    fbc:
      typeof window !== "undefined"
        ? (window.__META_FBC__ ?? getMetaCookie("_fbc"))
        : null,
    email: visitor.email ?? null,
    first_name: firstName,
    last_name: lastName,
  }).catch(() => {});

  return {
    meetingId: startRes.meeting_id,
    roomName: startRes.room_name,
    sessionId: startRes.session_id,
    livekitUrl: startRes.livekit_url,
    token: startRes.token,
    avatarEnabled: !!startRes.avatar_enabled,
    avatarId: startRes.avatar_id ?? null,
  };
}

export async function pauseMeeting(meetingId, sessionId) {
  return publicRequest(`/v1/public/meetings/${meetingId}/pause`, {
    method: "POST",
    headers: { "X-Session-ID": sessionId },
  });
}

export async function resumeMeeting(meetingId, sessionId) {
  return publicRequest(`/v1/public/meetings/${meetingId}/resume`, {
    method: "POST",
    headers: { "X-Session-ID": sessionId },
  });
}

export async function endMeeting(meetingId, sessionId) {
  return publicRequest(`/v1/public/meetings/${meetingId}/end`, {
    method: "POST",
    headers: { "X-Session-ID": sessionId },
  });
}

export async function signalVideoEnded(meetingId, sessionId) {
  return publicRequest(`/v1/public/meetings/${meetingId}/video-ended`, {
    method: "POST",
    headers: { "X-Session-ID": sessionId },
  });
}

/**
 * Fire-and-forget end meeting for page unload.
 * Uses fetch+keepalive since sendBeacon cannot set custom headers.
 */
export function endMeetingBeacon(meetingId, sessionId) {
  const base = getApiBaseUrl();
  const url = `${base.replace(/\/$/, "")}/v1/public/meetings/${meetingId}/end`;
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Session-ID": sessionId },
    keepalive: true,
  }).catch(() => {});
}

export function resolveMediaUrl(url) {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:")
  )
    return url;
  const base = getApiBaseUrl();
  if (base)
    return `${base.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
  return url;
}

/**
 * Send a Meta conversion event (anonymous, no workspace/user required).
 * Call from the widget when the host page provides meta data (e.g. fbp, fbc from Meta pixel).
 *
 * @param {string} eventName - Meta event name (e.g. 'Schedule', 'Lead', 'CompleteRegistration')
 * @param {Object} [payload] - Optional meta data
 * @param {string} [payload.fbp] - Facebook browser id (from _fbp cookie / fbq)
 * @param {string} [payload.fbc] - Facebook click id (from _fbc cookie / fbq)
 * @param {string} [payload.event_source_url] - Page URL (defaults to current location)
 * @param {number} [payload.event_time] - Unix timestamp (defaults to now)
 * @param {string} [payload.action_source] - e.g. 'website'
 * @param {string} [payload.client_user_agent] - Browser user agent (defaults to navigator.userAgent)
 * @param {string} [payload.email] - Visitor email (hashed server-side)
 * @param {string} [payload.first_name] - Visitor first name (hashed server-side)
 * @param {string} [payload.last_name] - Visitor last name (hashed server-side)
 * @param {string} [payload.phone_number] - Visitor phone (hashed server-side)
 * @param {Object} [payload.custom_data] - { utm_source, utm_medium, utm_campaign, utm_id, referrer }
 * @returns {Promise<{ status: Object, data: Object }>} - API response
 */
export async function sendMetaConversionEvent(eventName, payload = {}) {
  const body = {
    event_name: eventName,
    fbp: payload.fbp ?? null,
    fbc: payload.fbc ?? null,
    event_source_url:
      payload.event_source_url ??
      (typeof window !== "undefined" ? window.location?.href : null),
    event_time:
      payload.event_time ??
      (typeof Date.now === "function" ? Math.floor(Date.now() / 1000) : null),
    action_source: payload.action_source ?? "website",
    client_user_agent:
      payload.client_user_agent ??
      (typeof navigator !== "undefined" ? navigator.userAgent : null),
    email: payload.email ?? null,
    first_name: payload.first_name ?? null,
    last_name: payload.last_name ?? null,
    phone_number: payload.phone_number ?? null,
  };
  if (payload.custom_data && typeof payload.custom_data === "object") {
    body.custom_data = payload.custom_data;
  }
  return publicRequest(
    "/api/internal/meta/send_anonymous_event",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    getSalesBaseUrl(),
  );
}
